import { StepId, StepModel } from '../components/Steps/interfaces/Step';
import { getConfirmStatuses, isTicketReschedulable } from '../helpers/TicketHelpers';
import { TicketRoutePaths } from '../interfaces/Routes';
import { Ticket } from '../interfaces/Ticket';
import { isPathInRoutes } from './RouteService';

const getStepUri = (hashId: string | undefined, path: TicketRoutePaths): string => {
  return `${hashId ? '/' + hashId : ''}${path ? '/' + path : ''}`;
};

const getCustomerStep = (hashId: string, isAtConfirmation: boolean, isAtScheduling: boolean): StepModel => ({
  id: StepId.customer,
  isDisabled: isAtConfirmation,
  isCompleted: isAtScheduling || isAtConfirmation,
  uri: getStepUri(hashId, TicketRoutePaths.customer)
});

const getQuestionsStep = (hashId: string, isAtConfirmation: boolean, isAtScheduling: boolean): StepModel => ({
  id: StepId.questions,
  isDisabled: !isAtScheduling,
  isCompleted: isAtScheduling || isAtConfirmation,
  uri: getStepUri(hashId, TicketRoutePaths.questions)
});

const getAppointmentStep = (hashId: string, isNewTicket: boolean, isAtConfirmation: boolean): StepModel => ({
  id: StepId.appointment,
  isDisabled: isNewTicket || isAtConfirmation,
  isCompleted: isAtConfirmation,
  uri: getStepUri(hashId, TicketRoutePaths.appointment)
});

const getChangeStep = (hashId: string, isAtConfirmation: boolean): StepModel => ({
  id: StepId.change,
  isDisabled: !isAtConfirmation,
  disableUserNavigation: true,
  isCompleted: isAtConfirmation,
  uri: getStepUri(hashId, TicketRoutePaths.change)
});

const getConfirmStep = (hashId: string, isAtConfirmation: boolean): StepModel => ({
  id: StepId.confirm,
  isDisabled: !isAtConfirmation,
  isCompleted: isAtConfirmation,
  uri: getStepUri(hashId, TicketRoutePaths.confirm)
});

const confirmationStatuses = getConfirmStatuses();

const checkIsNewTicket = (ticket: Ticket) => {
  const { workOrder } = ticket;
  const { isQuestionnaireCompleted, status: workOrderStatus, appointment } = workOrder;
  const { isSchedulable } = appointment;
  const blocked = !isSchedulable;

  return ((workOrderStatus === 'New') || !workOrderStatus) && (!isQuestionnaireCompleted || blocked);
};

const checkAppointmentIsAtScheduling = (ticket: Ticket, isNewTicket: boolean) => {
  const { workOrder } = ticket;
  const { isQuestionnaireCompleted, appointment } = workOrder;
  const { status, arrivalWindowStart } = appointment;

  return !isNewTicket && (!arrivalWindowStart && !!isQuestionnaireCompleted)
    || (!!arrivalWindowStart && !confirmationStatuses.includes(status));
};

const getDefaultSteps = (ticket?: Ticket, isInErrorMode?: boolean): StepModel[] => {
  if (isInErrorMode) {
    return getErrorModeSteps();
  }

  if (!ticket) return [];

  const { hashId } = ticket;
  const { status } = ticket.workOrder.appointment;
  const isNewTicket = checkIsNewTicket(ticket);
  const isAtScheduling = checkAppointmentIsAtScheduling(ticket, isNewTicket);

  const isAtConfirmation = (!isNewTicket && !isAtScheduling) || confirmationStatuses.includes(status);

  return [
    getCustomerStep(hashId, isAtConfirmation, isAtScheduling),
    getQuestionsStep(hashId, isAtConfirmation, isAtScheduling),
    getThirdStep(ticket, isNewTicket, isAtConfirmation),
    getConfirmStep(hashId, isAtConfirmation)
  ];
};

const getThirdStep = (ticket: Ticket, isNewTicket: boolean, isAtConfirmation: boolean) => {
  const allowRescheduling = isTicketReschedulable(ticket);

  return allowRescheduling
    ? getChangeStep(ticket.hashId, isAtConfirmation)
    : getAppointmentStep(ticket.hashId, isNewTicket, isAtConfirmation);
};

const getErrorModeSteps = () => {
  return getStepsAs([
    getCustomerStep('', false, false),
    getQuestionsStep('', false, false),
    getAppointmentStep('', false, false),
    getConfirmStep('', false)
  ], ({ isDisabled: true, isCompleted: false }));
};

const getActiveStep = (steps: StepModel[]): StepModel => {
  const uncompletedSteps = steps.filter(step => !step.isCompleted);
  const areAllCompleted = uncompletedSteps.length === 0;
  const lastStep = steps[steps.length - 1];
  const activeStep = areAllCompleted ? lastStep : steps.find(step => !step.isDisabled && !step.isCompleted);

  return activeStep || steps[0];
};

type GetStepsAsOptions = Partial<Pick<StepModel, 'isCompleted' | 'isDisabled'>>;

const getStepsAs = (steps: StepModel[], options: GetStepsAsOptions): StepModel[] => {
  return steps.map(step => ({ ...step, ...options }));
};

const showSteps = (pathName: string) => {
  return !isPathInRoutes(pathName, [
    TicketRoutePaths.signature,
    TicketRoutePaths.confirm,
    TicketRoutePaths.cancel,
    TicketRoutePaths.meterkast,
    TicketRoutePaths.meterkastScheduled
  ]);
};

const showStepperBackground = (pathName: string) => {
  return showSteps(pathName) && !isPathInRoutes(pathName, [TicketRoutePaths.contact]);
};

export default {
  getDefaultSteps,
  getActiveStep,
  getStepsAs,
  showSteps,
  getStepUri,
  showStepperBackground,
};
